import { useMemo, useState, type ReactNode } from 'react';
import BoardGridDisplay from './BoardGridDisplay';
import { startHoldsFrame, type ClimbWall } from './climb-wall';
import { cropImageStyle, focusCrop, padRect } from '../hold-focus';

/**
 * How much of the wall around the start holds the crop keeps, as a fraction of
 * the holds' box: the circles drawn on them stay clear of the rounded corners,
 * and a hold or two around them says where on the wall they are.
 */
const START_PADDING = 0.15;

/** The fewest photo pixels the crop may span — the same floor as a photo row's (see ClimbRow). */
const MIN_CROP_PX = 96;

interface BoardClimbThumbnailProps {
  /** A wall with a photo and at least one hold — the caller falls back otherwise. */
  wall: ClimbWall;
  /** Drawn over the picture, e.g. the "New" tag. */
  children?: ReactNode;
}

/**
 * A board problem in a list, as the wall's photo zoomed to where it starts with
 * its holds drawn on. Every problem on a board shares the one photo, so the
 * whole wall shrunk to 64px would make forty identical squares; its start holds
 * — where the climber stands to begin it — are what tells one from the next,
 * the same reading a photo climb's row gives (see `startHoldsBounds`).
 *
 * The board is drawn at full size by `BoardGridDisplay`, blown up so the crop
 * alone fills the box and slid into place (`cropImageStyle`): one picture and
 * one overlay, so the circles cannot come away from the holds. Hidden until the
 * photo has loaded, since the crop is measured off its shape.
 */
export function BoardClimbThumbnail({ wall, children }: BoardClimbThumbnailProps) {
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);

  const crop = useMemo(() => {
    if (!natural) return null;
    const frame = startHoldsFrame(wall, natural.width / natural.height);
    return frame ? focusCrop(padRect(frame, START_PADDING), natural.width, natural.height, 1, MIN_CROP_PX) : null;
  }, [wall, natural]);

  return (
    <div
      data-testid="climb-row-board-thumbnail"
      className="relative h-16 w-16 shrink-0 overflow-hidden rounded-md bg-muted"
    >
      <div
        className={`pointer-events-none absolute ${crop ? '' : 'invisible inset-x-0 top-0'}`}
        style={cropImageStyle(crop)}
        aria-hidden
      >
        <BoardGridDisplay
          imageUrl={wall.imageUrl ?? ''}
          rows={wall.rows}
          columns={wall.columns}
          calibration={wall.calibration}
          holds={wall.holds}
          boardHolds={wall.boardHolds}
          extraHolds={wall.extraHolds}
          shapes={wall.shapes}
          scanned={wall.scanned}
          holdRadii={wall.holdRadii}
          onNaturalSize={setNatural}
        />
      </div>
      {children}
    </div>
  );
}
